import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import CategoryTopView from "./category-top-view";
import CategoryMobileDropdown from "./category-mobile-dropdown";
import { Panel } from "primereact/panel";
import { Button } from "primereact/button";
import CategoryService from "../../services/category/category.service";
import ProductService from "../../services/products/products.service";
import FlashNewsService, { FlashNewsData } from "../../services/banners/flash-news.service";
import { Suspense, lazy } from "react";
import AppLoading from "../../components/app-loading/app-loading";
import { mapSourceToTarget } from "../../models/category-new/catergory-mapper-model";
import { TreeNodeModel } from "../../models/category-new/category-tree-node";
import { ProductNewModel } from "../../models/products/product-new-model";
import AppFooter from "../layout/AppFooter";
import FlashNewsModal from "../banners/flash-news-modal";
import { Dialog } from "primereact/dialog";
import { Image } from "primereact/image";
import { useSharedProductLink } from "../../hooks/useSharedProductLink";
import ReferralBanner from "../referral/ReferralBanner";
import { useCustomerDelivery } from "../../providers/CustomerDeliveryProvider";
import { applyDeliveryAvailability } from "../../services/products/customer-delivery.service";

const ProductsCard = lazy(() => import("./products-card"));
const SelectedCategories = lazy(() => import("./selected-categories"));

interface FilterState {
  [key: string]: {
    checked: boolean;
    partialChecked: boolean;
  };
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const filterProductsByQuery = (
  products: any[],
  query: string,
  fields: string[] = [
    "productName",
    "name",
    "title",
    //"shortDescription",
    // "description",
    "categoryName",
    "tags",
    "sku",
  ]
) => {
  const normalized = (query || "").trim().toLowerCase();
  if (!normalized) return products;

  const tokens = normalized.split(/\s+/).filter(Boolean);

  const fieldToString = (obj: any, field: string) => {
    const v = obj[field];
    if (v === null || v === undefined) return "";
    if (Array.isArray(v)) return v.join(" ");
    return String(v);
  };

  const results = products
    .map((p) => {
      const searchable = fields.map((f) => fieldToString(p, f)).join(" ").toLowerCase();
      const name = (fieldToString(p, "productName") || fieldToString(p, "name") || "").toLowerCase();

      let score = 0;

      if (name === normalized) score += 200;
      else if (name.includes(normalized)) score += 100;

      const allTokensMatch = tokens.every((token) => {
        const wordRe = new RegExp("\\b" + escapeRegExp(token) + "\\b", "i");
        if (wordRe.test(searchable)) {
          if (wordRe.test(name)) score += 20;
          else score += 10;
          return true;
        }
        if (searchable.includes(token)) {
          if (name.includes(token)) score += 15;
          else score += 5;
          return true;
        }
        return false;
      });

      return { item: p, ok: allTokensMatch, score };
    })
    .filter((r) => r.ok)
    .sort((a, b) => b.score - a.score)
    .map((r) => r.item);

  return results;
};

const Products: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<ProductNewModel[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<ProductNewModel[]>([]);
  const delivery = useCustomerDelivery();
  const availableProducts = useMemo(() => applyDeliveryAvailability(filteredProducts, delivery.data, delivery.availabilityPending),
    [filteredProducts, delivery.data, delivery.availabilityPending]);
  const [categoriesNew, setCatergoriesNew] = useState<TreeNodeModel[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<FilterState>({});
  const [selectedCategoriesNew, setSelectedCategoriesNew] = useState<TreeNodeModel[]>([]);
  const [isDivVisible, setIsDivVisible] = useState(false);
  const [bannerVisible, setBannerVisible] = useState<boolean>(false);
  const [flashNewsData, setFlashNewsData] = useState<FlashNewsData | null>(null);
  const bannerActive = sessionStorage.getItem("bannerActive") || Date.now().toString();

  const [forceExact, setForceExact] = useState(false);
  const [exactIds, setExactIds] = useState<(number | string)[]>([]);

  // Ref to access ProductsCard methods
  const productsCardRef = useRef<any>(null);

  sessionStorage.setItem("bannerActive", bannerActive);

  // Load Flash News data from JSON
  useEffect(() => {
    FlashNewsService.getFlashNewsData().then((data) => {
      setFlashNewsData(data);
    }).catch((error) => {
      console.error('Error loading Flash News data:', error);
    });
  }, []);

  useEffect(() => {
    const bannerSeen = localStorage.getItem("bannerSeen");
    const storedSession = localStorage.getItem("bannerSession");
    const sessionBannerHidden = sessionStorage.getItem("bannerHidden");

    if (sessionBannerHidden === "true") {
      setBannerVisible(false);
    }
    else if (!bannerSeen || storedSession !== bannerActive) {
      setBannerVisible(true);
    }
  }, [bannerActive]);

  useEffect(() => {
    CategoryService.getAllCategories().then((response) => {
      setCatergoriesNew(mapSourceToTarget(response));
    });

    ProductService.getAllProductsForShipping().then((response) => {
      setProducts(response);
      setFilteredProducts(response);
    });
  }, []);

  const [searchQuery, setSearchQuery] = useState<string>("");

  // Read search from URL params and update searchQuery
  useEffect(() => {
    const searchFromUrl = searchParams.get("search") || "";
    setSearchQuery(searchFromUrl);
  }, [searchParams]);

  // Handle productId from URL for shared links using custom hook
  useSharedProductLink({
    products,
    onProductFound: (product) => {
      if (productsCardRef.current) {
        productsCardRef.current.showProductQuickView(product);
      }
    }
  });


  const handleSearch = (query: string) => {
    const normalizedQuery = (query || "").trim();
    setSearchQuery(normalizedQuery);
    // filterProducts will run due to effect below
  };

  const topCategorySelect = (category: TreeNodeModel | TreeNodeModel[] | null) => {
    // If category is null, clear all filters and show all products
    if (!category) {
      setSelectedKeys({});
      setSelectedCategoriesNew([]);
      setFilteredProducts(products);
      return;
    }

    // Handle both single category and array of categories
    const categories = Array.isArray(category) ? category : [category];

    const initFilterState: FilterState = categories.reduce((acc, cat) => {
      acc[cat.key] = { checked: true, partialChecked: false };

      // Add children if they exist
      if (cat.children) {
        cat.children.forEach((child) => {
          acc[child.key] = { checked: true, partialChecked: false };
        });
      }

      return acc;
    }, {} as FilterState);

    setSelectedKeys(initFilterState);
  };

  const removeChipCategory = (category: TreeNodeModel) => {
    // implement removal if needed later
  };

  const template = (options: any) => {
    const className = `${options.className} justify-content-start`;

    return (
      <>
        <div className={className}>
          <div className="p-inputgroup flex-1 filter-wrap">
            {selectedCategoriesNew.length > 0 ? (
              <>
                <label>Active Filters: </label>
                <SelectedCategories
                  categoriesData={selectedCategoriesNew}
                  topCategorySelect={removeChipCategory}
                />
              </>
            ) : null}
          </div>
        </div>
      </>
    );
  };

  // Re-run filtering when selected keys, products, categories or searchQuery change
  useEffect(() => {
    filterProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKeys, products, categoriesNew, searchQuery]);

  const collectSelectedCategories = (
    nodes: TreeNodeModel[],
    keys: FilterState
  ): TreeNodeModel[] => {
    let selected: TreeNodeModel[] = [];

    nodes.forEach((node) => {
      if (keys[node.key]?.checked) {
        selected.push(node);
      }

      if (node.children) {
        selected = selected.concat(collectSelectedCategories(node.children, keys));
      }
    });

    return selected;
  };

  const filterProducts = () => {
    const isObjectEmpty = Object.keys(selectedKeys).length === 0;
    const normalizedSearch = (searchQuery || "").trim();

    if (isObjectEmpty) {
      if (normalizedSearch !== "") {
        const searched = filterProductsByQuery(products, normalizedSearch, [
          "productName",
          "name",
          "title",
          "categoryName",
          "tags",
          "sku",
        ]);
        setFilteredProducts(searched);
      } else {
        setFilteredProducts(products);
      }
      setSelectedCategoriesNew([]);
      setForceExact(false);
      setExactIds([]);
    } else {
      // there are category selections
      const filteredProductsList = products.filter((product) => {
        const categoryKey = `${(product as any).categoryID} - Category`;
        const subCategoryKey = `${(product as any).subCategoryID} - SubCategory`;

        const isSubCategorySelected = selectedKeys[subCategoryKey]?.checked;
        const isCategorySelected = selectedKeys[categoryKey]?.checked;

        if (isSubCategorySelected) {
          return true;
        } else if (isCategorySelected) {
          return true;
        }
        return false;
      });

      if (normalizedSearch !== "") {
        // when search active, first narrow the product set by the selected categories, then search inside them
        const searchedInsideCategory = filterProductsByQuery(filteredProductsList, normalizedSearch, [
          "productName",
          "name",
          "title",
          "categoryName",
          "tags",
          "sku",
        ]);
        setFilteredProducts(searchedInsideCategory);
      } else {
        setFilteredProducts(filteredProductsList);
      }

      const selectedCategories = collectSelectedCategories(categoriesNew, selectedKeys);
      setSelectedCategoriesNew(selectedCategories);

      // When search is active -> derive exact leaf IDs and enforce exact filtering
      if (normalizedSearch !== "") {
        // prefer leaf nodes (without children)
        const leafNodes = selectedCategories.filter((n) => !n.children || n.children.length === 0);
        const nodesToUse = leafNodes.length > 0 ? leafNodes : selectedCategories;

        // derive ids robustly: prefer node.data?.id, fallback to parsing key
        const ids = nodesToUse.map((n) => {
          // try data.id
          // @ts-ignore
          if (n.data && (n.data.id !== undefined && n.data.id !== null)) {
            // @ts-ignore
            return String(n.data.id);
          }
          // fallback to key like "123 - SubCategory" or "456 - Category"
          try {
            return String(String(n.key).split(" - ")[0]);
          } catch (err) {
            return String(n.key);
          }
        });

        setExactIds(ids);
        setForceExact(true);

        // apply exact filter: product.categoryID === id OR product.subCategoryID === id
        const exactFiltered = filteredProductsList
          .filter((p) => {
            const prodCatId = String((p as any).categoryID ?? "");
            const prodSubCatId = String((p as any).subCategoryID ?? "");
            return ids.includes(prodCatId) || ids.includes(prodSubCatId);
          })
          .filter((p) => {
            // search inside exact-matched products to respect the searchQuery
            return filterProductsByQuery([p], normalizedSearch, [
              "productName",
              "name",
              "title",
              "categoryName",
              "tags",
              "sku",
            ]).length > 0;
          });

        setFilteredProducts(exactFiltered);
      } else {
        setForceExact(false);
        setExactIds([]);
      }
    }
  };

  const clearFilters = () => {
    setSelectedCategoriesNew([]);
    setSelectedKeys({});
    setForceExact(false);
    setExactIds([]);
    setFilteredProducts(products);
  };

  function handelOnHide() {
    setBannerVisible(false);
    sessionStorage.setItem("bannerHidden", "true");
    localStorage.setItem("bannerSeen", "true");
    localStorage.setItem("bannerSession", bannerActive);
  }

  return (
    <>
      <Suspense fallback={<AppLoading />}>
        <div className="grid layout-content">
          <div className="col-12">
            {categoriesNew.length > 0 ? (
              <CategoryTopView
                categoriesData={categoriesNew}
                topCategorySelect={topCategorySelect}
                data={products}
                onSearch={handleSearch}
              />
            ) : null}
          </div>

          {/* Mobile Category Dropdown */}
          <div className="col-12 xs-view-only">
            {categoriesNew.length > 0 ? (
              <CategoryMobileDropdown
                categoriesData={categoriesNew}
                selectedKeys={selectedKeys}
                setSelectedKeys={setSelectedKeys}
                topCategorySelect={topCategorySelect}
              />
            ) : null}
          </div>

          <FlashNewsModal
            visible={bannerVisible}
            onHide={handelOnHide}
            data={flashNewsData || {
              title: "Sanjeevini",
              subtitle: "Kashaya powder",
              image: "https://farm2me.azurewebsites.net/images/farm2me/Banner.jpg",
              // features: [
              //   "Supports Diabetic Health",
              //   //"Balances BP & Heart Health",
              //   //"Improves Digestion & Gut Strength",
              //   //"Boosts Immunity Naturally",
              //   //"Anti-Inflammatory & Detoxifying",
              // ],
              cta: {
                label: "Shop Now",
                description: "Begin each day with the designated millet pack on an empty stomach",
              },
            }}
          />

          {/* Referral Banner */}
          <div className="col-12">
            <ReferralBanner />
          </div>

          <div className="col-12 flex xs">
            <div className="flex-grow-1 flex">
              <div className="w-full">
                {/* <div className="products-header">
                  <span className="info-text">
                    There are{" "}
                    {filteredProducts.length > 0
                      ? filteredProducts.length
                      : 0}{" "}
                    products.
                  </span>
                </div> */}

                {/* <Panel headerTemplate={template}>
                  {filteredProducts.length > 0 ? ( */}
                {delivery.data?.requiresPincode && <div role="status" className="p-3 mb-2 border-round surface-100">
                  Enter a delivery pincode using the location button above to check product availability.
                </div>}
                {delivery.error && <div role="alert" className="p-3 mb-2 border-round surface-100">
                  {delivery.error} <Button label="Retry availability" icon="pi pi-refresh" text
                    loading={delivery.loading} onClick={() => { void delivery.refresh(); }} />
                </div>}
                <ProductsCard ref={productsCardRef} productsData={availableProducts} catalogProducts={products} />
                {/* ) : null}
                </Panel> */}
              </div>
            </div>
          </div>
        </div>
        <AppFooter />
      </Suspense>
    </>
  );
};

export default Products;
